import { useEffect, useState } from 'react';
import {
  User, Lock, Bell, Monitor, Loader2, Check, AlertCircle, Building2, Info, LogOut,
} from 'lucide-react';
import * as api from '../lib/api';
import { useAuthStore } from '../store/useAuthStore';
import { formatMonthYear } from '../lib/format';
import AvatarField from '../components/profile/AvatarField';

/**
 * One profile screen for every role, reached from the user menu in any console.
 *
 * Sections are driven by what the server returns, not by branching on role in
 * the client: staff get facility assignments because the API sends them, and
 * the notification switches are whatever the API says apply. Adding a
 * preference server-side surfaces here with no client change.
 */
export default function ProfilePage() {
  const [data, setData] = useState(null);
  const [state, setState] = useState('loading');
  const [error, setError] = useState(null);
  const bootstrap = useAuthStore((s) => s.bootstrap);

  const load = async () => {
    setState('loading');
    try {
      setData(await api.getProfile());
      setState('ready');
    } catch (err) {
      setError(err.message);
      setState('error');
    }
  };

  useEffect(() => { load(); }, []);

  if (state === 'loading') {
    return <div className="grid place-items-center py-20"><Loader2 className="h-6 w-6 animate-spin text-brand-600" aria-hidden="true" /></div>;
  }
  if (state === 'error') {
    return (
      <div className="mx-auto max-w-lg px-4 py-16 text-center">
        <AlertCircle className="mx-auto h-8 w-8 text-rose-400" aria-hidden="true" />
        <p className="mt-3 text-sm font-bold text-slate-900">Couldn&apos;t load your profile</p>
        <p className="mt-1 text-sm text-slate-500">{error}</p>
        <button type="button" onClick={load} className="btn-ghost mx-auto mt-4">Try again</button>
      </div>
    );
  }

  const { user, notification_preferences: prefs, email_change: emailChange } = data;

  return (
    <div className="mx-auto max-w-3xl px-4 py-8 sm:px-6">
      <h1 className="text-2xl font-extrabold tracking-tight text-slate-900">Your account</h1>
      <p className="mt-1 text-sm text-slate-600">
        {user.role === 'customer' ? 'Tenant' : 'Staff'} account · member since {formatMonthYear(user.created_at)}
      </p>

      <div className="mt-6 space-y-6">
        <AvatarField user={user} onChanged={() => { load(); bootstrap(); }} />
        <DetailsCard user={user} emailChange={emailChange} onSaved={() => { load(); bootstrap(); }} />
        {user.role !== 'customer' && <AssignmentsCard assignments={user.facility_assignments} />}
        <PreferencesCard preferences={prefs} onSaved={load} />
        <PasswordCard />
        <SessionsCard />
      </div>
    </div>
  );
}

function Card({ Icon, title, hint, children }) {
  return (
    <section className="rounded-xl border border-slate-200 bg-white p-5">
      <div className="flex items-start gap-3">
        <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-brand-50 text-brand-700">
          <Icon className="h-4 w-4" aria-hidden="true" />
        </span>
        <div className="min-w-0 flex-1">
          <h2 className="text-sm font-extrabold text-slate-900">{title}</h2>
          {hint && <p className="mt-0.5 text-xs text-slate-500">{hint}</p>}
          <div className="mt-4">{children}</div>
        </div>
      </div>
    </section>
  );
}

function DetailsCard({ user, emailChange, onSaved }) {
  const [form, setForm] = useState({
    first_name: user.first_name, last_name: user.last_name, phone: user.phone ?? '',
  });
  const [status, setStatus] = useState(null);
  const [saving, setSaving] = useState(false);

  const submit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setStatus(null);
    try {
      await api.updateProfile(form);
      setStatus({ ok: true, message: 'Saved' });
      onSaved();
    } catch (err) {
      setStatus({ ok: false, message: err.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card Icon={User} title="Your details" hint="How we address you, and how staff reach you.">
      <form onSubmit={submit}>
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label className="field-label mb-1" htmlFor="first_name">First name</label>
            <input id="first_name" required maxLength={80} className="field-input"
                   value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} />
          </div>
          <div>
            <label className="field-label mb-1" htmlFor="last_name">Last name</label>
            <input id="last_name" required maxLength={80} className="field-input"
                   value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} />
          </div>
        </div>

        <label className="field-label mb-1 mt-3" htmlFor="phone">Phone</label>
        <input id="phone" type="tel" maxLength={32} className="field-input"
               value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />

        <label className="field-label mb-1 mt-3" htmlFor="email">Email (sign-in)</label>
        <input id="email" className="field-input bg-slate-50 text-slate-500" value={user.email}
               readOnly disabled aria-describedby="email-why" />
        {/* Say why it is locked. A disabled field with no explanation reads as a bug. */}
        <p id="email-why" className="mt-1 flex items-start gap-1.5 text-[12px] text-slate-500">
          <Info className="mt-0.5 h-3 w-3 shrink-0" aria-hidden="true" />
          {emailChange.reason}
        </p>

        <div className="mt-4 flex items-center gap-3">
          <button type="submit" disabled={saving} className="btn-primary">
            {saving && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
            Save changes
          </button>
          {status && (
            <span role="status" className={`inline-flex items-center gap-1 text-xs font-semibold ${status.ok ? 'text-emerald-700' : 'text-rose-700'}`}>
              {status.ok ? <Check className="h-3.5 w-3.5" aria-hidden="true" /> : <AlertCircle className="h-3.5 w-3.5" aria-hidden="true" />}
              {status.message}
            </span>
          )}
        </div>
      </form>
    </Card>
  );
}

function AssignmentsCard({ assignments }) {
  return (
    <Card Icon={Building2} title="Facility access"
          hint="Set by your owner — you cannot change these yourself.">
      {assignments.length === 0 ? (
        <p className="text-sm text-slate-500">
          No facilities assigned. You&apos;ll see them here once an owner adds you.
        </p>
      ) : (
        <ul className="flex flex-wrap gap-2">
          {assignments.map((a) => (
            <li key={a.name} className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-semibold text-slate-700">
              <Building2 className="h-3.5 w-3.5 text-brand-500" aria-hidden="true" />
              {a.name}
              <span className="text-slate-400">{a.city}, {a.state_code}</span>
              {a.is_primary && <span className="text-brand-700">· primary</span>}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function PreferencesCard({ preferences, onSaved }) {
  const [saving, setSaving] = useState(null);
  const [error, setError] = useState(null);

  const toggle = async (pref) => {
    setSaving(pref.key);
    setError(null);
    try {
      await api.updatePreferences({ [pref.key]: !pref.enabled });
      onSaved();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(null);
    }
  };

  return (
    <Card Icon={Bell} title="Notifications" hint="What we contact you about.">
      {/* Honest rather than flattering: these are recorded but nothing sends yet. */}
      <p className="mb-3 flex items-start gap-1.5 rounded-lg bg-amber-50 p-2.5 text-[12px] leading-snug text-amber-900">
        <Info className="mt-0.5 h-3 w-3 shrink-0" aria-hidden="true" />
        Email delivery isn&apos;t switched on yet. Your choices are saved now and will be
        honoured the moment it is — you won&apos;t be opted into anything you turned off here.
      </p>

      {error && <p role="alert" className="mb-3 rounded-lg bg-rose-50 p-2.5 text-xs text-rose-800">{error}</p>}

      <ul className="divide-y divide-slate-100">
        {preferences.map((pref) => (
          <li key={pref.key} className="flex items-center justify-between gap-4 py-2.5">
            <span className="text-sm text-slate-700">{pref.label}</span>
            <button
              type="button"
              role="switch"
              aria-checked={pref.enabled}
              aria-label={pref.label}
              disabled={saving === pref.key}
              onClick={() => toggle(pref)}
              className={`relative h-6 w-11 shrink-0 rounded-full transition ${pref.enabled ? 'bg-brand-600' : 'bg-slate-300'} disabled:opacity-50`}
            >
              <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all ${pref.enabled ? 'left-[1.375rem]' : 'left-0.5'}`} />
            </button>
          </li>
        ))}
      </ul>
    </Card>
  );
}

function PasswordCard() {
  const [form, setForm] = useState({ current: '', next: '', confirm: '' });
  const [status, setStatus] = useState(null);
  const [saving, setSaving] = useState(false);

  const submit = async (event) => {
    event.preventDefault();
    if (form.next !== form.confirm) {
      setStatus({ ok: false, message: 'The two new passwords do not match' });
      return;
    }
    setSaving(true);
    setStatus(null);
    try {
      const result = await api.changePassword(form.current, form.next);
      setForm({ current: '', next: '', confirm: '' });
      setStatus({
        ok: true,
        message: result.other_sessions_revoked > 0
          ? `Password changed. Signed out of ${result.other_sessions_revoked} other device${result.other_sessions_revoked === 1 ? '' : 's'}.`
          : 'Password changed.',
      });
    } catch (err) {
      setStatus({ ok: false, message: err.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card Icon={Lock} title="Password"
          hint="Changing it signs out every other device, but keeps you signed in here.">
      <form onSubmit={submit}>
        <label className="field-label mb-1" htmlFor="current_password">Current password</label>
        <input id="current_password" type="password" autoComplete="current-password" required
               className="field-input mb-3" value={form.current}
               onChange={(e) => setForm({ ...form, current: e.target.value })} />

        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label className="field-label mb-1" htmlFor="new_password">New password</label>
            <input id="new_password" type="password" autoComplete="new-password" required minLength={10}
                   className="field-input" value={form.next}
                   onChange={(e) => setForm({ ...form, next: e.target.value })} />
          </div>
          <div>
            <label className="field-label mb-1" htmlFor="confirm_password">Confirm</label>
            <input id="confirm_password" type="password" autoComplete="new-password" required minLength={10}
                   className="field-input" value={form.confirm}
                   onChange={(e) => setForm({ ...form, confirm: e.target.value })} />
          </div>
        </div>
        <p className="mt-1 text-[12px] text-slate-500">At least 10 characters. A short phrase beats a short scramble.</p>

        <div className="mt-4 flex items-center gap-3">
          <button type="submit" disabled={saving} className="btn-primary">
            {saving && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
            Change password
          </button>
          {status && (
            <span role="status" className={`inline-flex items-center gap-1 text-xs font-semibold ${status.ok ? 'text-emerald-700' : 'text-rose-700'}`}>
              {status.ok ? <Check className="h-3.5 w-3.5" aria-hidden="true" /> : <AlertCircle className="h-3.5 w-3.5" aria-hidden="true" />}
              {status.message}
            </span>
          )}
        </div>
      </form>
    </Card>
  );
}

function SessionsCard() {
  const [sessions, setSessions] = useState([]);
  const [state, setState] = useState('loading');
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);

  const load = async () => {
    try {
      const { sessions: list } = await api.listMySessions();
      setSessions(list);
      setState('ready');
    } catch (err) {
      setError(err.message);
      setState('error');
    }
  };

  useEffect(() => { load(); }, []);

  const revoke = async (session) => {
    setBusy(session.id);
    setError(null);
    try {
      await api.revokeSession(session.id);
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(null);
    }
  };

  return (
    <Card Icon={Monitor} title="Where you're signed in"
          hint="If you don't recognise one, sign it out and change your password.">
      {error && <p role="alert" className="mb-3 rounded-lg bg-rose-50 p-2.5 text-xs text-rose-800">{error}</p>}
      {state === 'loading' && <p className="text-sm text-slate-500">Loading…</p>}

      <ul className="divide-y divide-slate-100">
        {sessions.map((s) => (
          <li key={s.id} className="flex items-center justify-between gap-4 py-2.5">
            <div className="min-w-0">
              <p className="text-sm font-semibold text-slate-800">
                {s.device}
                {s.is_current && (
                  <span className="ml-2 rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-bold text-emerald-800">
                    This device
                  </span>
                )}
              </p>
              <p className="text-[12px] text-slate-500">
                {s.ip_address ?? 'unknown address'} · last used{' '}
                {new Date(s.last_used_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
              </p>
            </div>
            {!s.is_current && (
              <button type="button" onClick={() => revoke(s)} disabled={busy === s.id}
                      className="btn-ghost shrink-0 py-1.5 text-xs">
                <LogOut className="h-3.5 w-3.5" aria-hidden="true" /> Sign out
              </button>
            )}
          </li>
        ))}
      </ul>
    </Card>
  );
}
